import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import couponService, { CouponDiscountType, UpdateCouponDTO } from '../../services/couponService';
import { getServerErrorMessage } from '../../utils/errorUtils';
import '../../styles/ImsTheme.css';
import './CouponFormPage.css';
import { ArrowLeft, Save } from 'lucide-react';

const MAX_LENGTHS = {
  code: 40,
  description: 200,
};

interface CouponForm {
  code: string;
  description: string;
  discountType: CouponDiscountType;
  discountValue: number | '';
  minOrderAmount: number | '';
  maxDiscountAmount: number | '';
  validFrom: string;
  validTo: string;
  usageLimit: number | '';
  isActive: boolean;
}

interface FieldErrors {
  code?: string;
  description?: string;
  discountValue?: string;
  minOrderAmount?: string;
  maxDiscountAmount?: string;
  validFrom?: string;
  validTo?: string;
  usageLimit?: string;
}

const NUMERIC_FIELDS = ['discountValue', 'minOrderAmount', 'maxDiscountAmount', 'usageLimit'];

/** Server sends UTC instants; the date inputs need a plain yyyy-MM-dd value. */
const toDateInput = (iso: string) => new Date(iso).toISOString().slice(0, 10);

const mapFormToDTO = (form: CouponForm): UpdateCouponDTO => ({
  code: form.code.trim().toUpperCase(),
  description: form.description || undefined,
  discountType: form.discountType,
  discountValue: Number(form.discountValue),
  minOrderAmount: form.minOrderAmount === '' ? undefined : Number(form.minOrderAmount),
  maxDiscountAmount: form.maxDiscountAmount === '' ? undefined : Number(form.maxDiscountAmount),
  validFrom: new Date(`${form.validFrom}T00:00:00Z`).toISOString(),
  validTo: new Date(`${form.validTo}T23:59:59Z`).toISOString(),
  usageLimit: form.usageLimit === '' ? undefined : Number(form.usageLimit),
  isActive: form.isActive,
});

const CouponEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [usageCount, setUsageCount] = useState(0);
  const [formData, setFormData] = useState<CouponForm>({
    code: '',
    description: '',
    discountType: CouponDiscountType.Percentage,
    discountValue: '',
    minOrderAmount: '',
    maxDiscountAmount: '',
    validFrom: '',
    validTo: '',
    usageLimit: '',
    isActive: true,
  });

  const isPercentage = formData.discountType === CouponDiscountType.Percentage;

  const loadCoupon = useCallback(async () => {
    if (!id) return;
    setLoadingData(true);
    setError(null);
    try {
      const coupon = await couponService.getById(Number(id));
      if (!coupon) {
        setError(t('coupon.notFound'));
        return;
      }
      setUsageCount(coupon.usageCount);
      setFormData({
        code: coupon.code,
        description: coupon.description ?? '',
        discountType: coupon.discountType,
        discountValue: coupon.discountValue,
        minOrderAmount: coupon.minOrderAmount ?? '',
        maxDiscountAmount: coupon.maxDiscountAmount ?? '',
        validFrom: toDateInput(coupon.validFrom),
        validTo: toDateInput(coupon.validTo),
        usageLimit: coupon.usageLimit ?? '',
        isActive: coupon.isActive,
      });
    } catch (err) {
      setError(getServerErrorMessage(err) || t('coupon.failedToLoad'));
    } finally {
      setLoadingData(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadCoupon();
  }, [loadCoupon]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === 'checkbox';

    let finalValue: any;
    if (isCheckbox) {
      finalValue = (e.target as HTMLInputElement).checked;
    } else if (name === 'discountType') {
      finalValue = Number(value) as CouponDiscountType;
    } else if (NUMERIC_FIELDS.includes(name)) {
      finalValue = value === '' ? '' : Number(value);
    } else {
      finalValue = value;
      if (name in MAX_LENGTHS && value.length > MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]) {
        finalValue = value.substring(0, MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]);
      }
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));

    if (!isCheckbox) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    if (!formData.code.trim()) {
      errors.code = t('coupon.formValidation.codeRequired');
      isValid = false;
    } else if (formData.code.length > MAX_LENGTHS.code) {
      errors.code = t('coupon.formValidation.codeMaxLength', { max: MAX_LENGTHS.code });
      isValid = false;
    }

    if (formData.description && formData.description.length > MAX_LENGTHS.description) {
      errors.description = t('coupon.formValidation.descriptionMaxLength', { max: MAX_LENGTHS.description });
      isValid = false;
    }

    if (formData.discountValue === '' || formData.discountValue === null) {
      errors.discountValue = t('coupon.formValidation.discountRequired');
      isValid = false;
    } else if (Number(formData.discountValue) <= 0) {
      errors.discountValue = t('coupon.formValidation.discountPositive');
      isValid = false;
    } else if (isPercentage && Number(formData.discountValue) > 100) {
      errors.discountValue = t('coupon.formValidation.discountPercentMax');
      isValid = false;
    }

    if (formData.minOrderAmount !== '' && Number(formData.minOrderAmount) < 0) {
      errors.minOrderAmount = t('coupon.formValidation.minOrderNegative');
      isValid = false;
    }

    if (formData.maxDiscountAmount !== '' && Number(formData.maxDiscountAmount) < 0) {
      errors.maxDiscountAmount = t('coupon.formValidation.maxDiscountNegative');
      isValid = false;
    }

    if (!formData.validFrom) {
      errors.validFrom = t('coupon.formValidation.validFromRequired');
      isValid = false;
    }

    if (!formData.validTo) {
      errors.validTo = t('coupon.formValidation.validToRequired');
      isValid = false;
    } else if (formData.validFrom && formData.validTo < formData.validFrom) {
      errors.validTo = t('coupon.formValidation.validToBeforeFrom');
      isValid = false;
    }

    if (formData.usageLimit !== '') {
      if (Number(formData.usageLimit) < 1) {
        errors.usageLimit = t('coupon.formValidation.usageLimitMin');
        isValid = false;
      } else if (Number(formData.usageLimit) < usageCount) {
        // Mirrors the server rule — a cap below what has already been redeemed
        // would make the remaining-usage arithmetic negative.
        errors.usageLimit = t('coupon.formValidation.usageLimitBelowRedeemed', { count: usageCount });
        isValid = false;
      }
    }

    setFieldErrors(errors);

    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    setSaving(true);
    try {
      await couponService.update(Number(id), mapFormToDTO(formData));
      navigate('/sales/coupons', { state: { message: t('coupon.updateSuccess') } });
    } catch (err) {
      setError(getServerErrorMessage(err) || t('coupon.failedToUpdate'));
      setSaving(false);
    }
  };

  if (loadingData) {
    return (
      <Layout>
        <div className="edit-container coupon-form-page">
          <div className="ims-loading">{t('common.loading')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="edit-container coupon-form-page">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/sales/coupons')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{t('coupon.editCoupon')}</div>
          </div>
          <div className="ims-top-bar-right">
            <button
              type="button"
              className="ims-btn ims-btn-secondary"
              onClick={() => navigate('/sales/coupons')}
              disabled={saving}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={saving}>
              {saving
                ? <><span className="ims-btn-spinner" />{t('common.saving')}</>
                : <><Save size={15} strokeWidth={2} />{t('common.save')}</>}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="edit-content">
          <div className="edit-form-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h2 className="edit-card-title">{formData.code}</h2>
              </div>
              <div className="edit-card-body">
                <form id="page-form" onSubmit={handleSubmit} noValidate>
                  <div className="form-group">
                    <label className="form-label">
                      {t('coupon.code')} <span className="required">*</span>
                      <span className="char-counter">{formData.code.length}/{MAX_LENGTHS.code}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input coupon-code-input ${fieldErrors.code ? 'is-invalid' : ''}`}
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.code}
                      placeholder="SAVE10"
                    />
                    {fieldErrors.code && <small className="form-error">{fieldErrors.code}</small>}
                    {!fieldErrors.code && <small className="form-helper">{t('coupon.codeHint')}</small>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('coupon.description')}
                      <span className="char-counter">{formData.description.length}/{MAX_LENGTHS.description}</span>
                    </label>
                    <textarea
                      className={`form-textarea ${fieldErrors.description ? 'is-invalid' : ''}`}
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.description}
                      placeholder={t('coupon.description')}
                      rows={3}
                    />
                    {fieldErrors.description && <small className="form-error">{fieldErrors.description}</small>}
                    {!fieldErrors.description && <small className="form-helper">{t('coupon.descriptionHint')}</small>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('coupon.discountType')} <span className="required">*</span>
                    </label>
                    <select
                      className="form-select"
                      name="discountType"
                      value={formData.discountType}
                      onChange={handleInputChange}
                    >
                      <option value={CouponDiscountType.Percentage}>{t('coupon.percentage')}</option>
                      <option value={CouponDiscountType.FlatAmount}>{t('coupon.flatAmount')}</option>
                    </select>
                    <small className="form-helper">{t('coupon.discountTypeHint')}</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('coupon.discountValue')} <span className="required">*</span>
                    </label>
                    <div className="coupon-input-with-suffix">
                      <input
                        type="number"
                        className={`form-input ${fieldErrors.discountValue ? 'is-invalid' : ''}`}
                        name="discountValue"
                        value={formData.discountValue}
                        onChange={handleInputChange}
                        placeholder="0"
                        step="0.01"
                        min="0"
                      />
                      <span className="coupon-input-suffix">{isPercentage ? '%' : t('coupon.amount')}</span>
                    </div>
                    {fieldErrors.discountValue && <small className="form-error">{fieldErrors.discountValue}</small>}
                    {!fieldErrors.discountValue && (
                      <small className="form-helper">
                        {isPercentage ? t('coupon.discountValuePercentHint') : t('coupon.discountValueFlatHint')}
                      </small>
                    )}
                  </div>

                  <div className="coupon-form-row">
                    <div className="form-group">
                      <label className="form-label">{t('coupon.minOrderAmount')}</label>
                      <input
                        type="number"
                        className={`form-input ${fieldErrors.minOrderAmount ? 'is-invalid' : ''}`}
                        name="minOrderAmount"
                        value={formData.minOrderAmount}
                        onChange={handleInputChange}
                        placeholder={t('coupon.noMinimum')}
                        step="0.01"
                        min="0"
                      />
                      {fieldErrors.minOrderAmount && <small className="form-error">{fieldErrors.minOrderAmount}</small>}
                      {!fieldErrors.minOrderAmount && <small className="form-helper">{t('coupon.minOrderAmountHint')}</small>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">{t('coupon.maxDiscountAmount')}</label>
                      <input
                        type="number"
                        className={`form-input ${fieldErrors.maxDiscountAmount ? 'is-invalid' : ''}`}
                        name="maxDiscountAmount"
                        value={formData.maxDiscountAmount}
                        onChange={handleInputChange}
                        placeholder={t('coupon.noCap')}
                        step="0.01"
                        min="0"
                        disabled={!isPercentage}
                      />
                      {fieldErrors.maxDiscountAmount && <small className="form-error">{fieldErrors.maxDiscountAmount}</small>}
                      {!fieldErrors.maxDiscountAmount && (
                        <small className="form-helper">
                          {isPercentage ? t('coupon.maxDiscountAmountHint') : t('coupon.maxDiscountOnlyPercentage')}
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="coupon-form-row">
                    <div className="form-group">
                      <label className="form-label">
                        {t('coupon.validFrom')} <span className="required">*</span>
                      </label>
                      <input
                        type="date"
                        className={`form-input ${fieldErrors.validFrom ? 'is-invalid' : ''}`}
                        name="validFrom"
                        value={formData.validFrom}
                        onChange={handleInputChange}
                      />
                      {fieldErrors.validFrom && <small className="form-error">{fieldErrors.validFrom}</small>}
                      {!fieldErrors.validFrom && <small className="form-helper">{t('coupon.validFromHint')}</small>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t('coupon.validTo')} <span className="required">*</span>
                      </label>
                      <input
                        type="date"
                        className={`form-input ${fieldErrors.validTo ? 'is-invalid' : ''}`}
                        name="validTo"
                        value={formData.validTo}
                        onChange={handleInputChange}
                      />
                      {fieldErrors.validTo && <small className="form-error">{fieldErrors.validTo}</small>}
                      {!fieldErrors.validTo && <small className="form-helper">{t('coupon.validToHint')}</small>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('coupon.usageLimit')}</label>
                    <input
                      type="number"
                      className={`form-input ${fieldErrors.usageLimit ? 'is-invalid' : ''}`}
                      name="usageLimit"
                      value={formData.usageLimit}
                      onChange={handleInputChange}
                      placeholder={t('coupon.unlimited')}
                      step="1"
                      min="1"
                    />
                    {fieldErrors.usageLimit && <small className="form-error">{fieldErrors.usageLimit}</small>}
                    {!fieldErrors.usageLimit && <small className="form-helper">{t('coupon.usageLimitHint')}</small>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('coupon.timesRedeemed')}</label>
                    <div className="coupon-usage-readonly">
                      <span className="coupon-usage-value">{usageCount}</span>
                      <span className="form-helper">{t('coupon.timesRedeemedHint')}</span>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-checkbox-label">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                      <span>{t('coupon.isActive')}</span>
                    </label>
                    <small className="form-helper">{t('coupon.isActiveHint')}</small>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="edit-info-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('common.information')}</h3>
              </div>
              <div className="edit-card-body">
                <div className="help-item">
                  <strong>{t('coupon.timesRedeemed')}</strong>
                  <p>{t('coupon.help.timesRedeemed')}</p>
                </div>
                <div className="help-item">
                  <strong>{t('coupon.usageLimit')}</strong>
                  <p>{t('coupon.help.usageLimitEdit')}</p>
                </div>
                <div className="help-item">
                  <strong>{t('coupon.isActive')}</strong>
                  <p>{t('coupon.help.isActive')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CouponEditPage;
